import React, { useState } from 'react';
import ReactECharts from 'echarts-for-react';

// InfluxDB data point
interface InfluxTempData {
  time: number; // timestamp in milliseconds
  value: number; // temperature in celsius
  label: string; // sensor name
}

// Athena anomaly marker
interface AthenaAnomaly {
  time: string; // ISO timestamp
  temp_c: number;
  temp_status: string;
  predicted_min_c: number;
  predicted_max_c: number;
}

interface RawTemperatureTimelineChartProps {
  influxData: InfluxTempData[];
  athenaAnomalies: AthenaAnomaly[];
  timeRange: string;
}

const RawTemperatureTimelineChart: React.FC<RawTemperatureTimelineChartProps> = ({ 
  influxData,
  athenaAnomalies,
  timeRange
}) => {
  const [showInlet, setShowInlet] = useState(false);
  const [showOutlet, setShowOutlet] = useState(true);
  const [showPower, setShowPower] = useState(false);

  // Separate InfluxDB data by sensor
  const outletData = influxData.filter(d => d.label.toLowerCase().includes('compressor outlet'));
  const inletData = influxData.filter(d => d.label.toLowerCase().includes('compressor inlet'));
  const powerData = influxData.filter(d => d.label.toLowerCase().includes('power'));

  // Convert InfluxDB data to chart format (Celsius to Fahrenheit)
  const convertInfluxToChart = (data: InfluxTempData[]) => {
    return data.map(d => ({
      x: d.time,
      y: d.value * 9/5 + 32, // Convert Celsius to Fahrenheit
      temp_c: d.value
    }));
  };

  const outletChartData = convertInfluxToChart(outletData);
  const inletChartData = convertInfluxToChart(inletData);
  const powerChartData = powerData.map(d => ({
    x: d.time,
    y: d.value // Power is already in correct units
  }));

  // Convert Athena anomalies to chart markers
  const anomalyMarkers = athenaAnomalies.map(a => {
    const isoTime = a.time.toString().includes(' UTC') 
      ? a.time.replace(' UTC', 'Z').replace(' ', 'T')
      : a.time;
    const timestamp = new Date(isoTime).getTime();
    const tempC = typeof a.temp_c === 'string' ? parseFloat(a.temp_c) : a.temp_c;
    const tempF = tempC * 9/5 + 32;
    
    return {
      x: timestamp,
      y: tempF,
      status: a.temp_status,
      predicted_min_f: a.predicted_min_c * 9/5 + 32,
      predicted_max_f: a.predicted_max_c * 9/5 + 32
    };
  });

  const allTemps = [
    ...outletChartData.map(d => d.y),
    ...(showInlet ? inletChartData.map(d => d.y) : [])
  ];
  const minTemp = allTemps.length > 0 ? Math.min(...allTemps) : 0;
  const maxTemp = allTemps.length > 0 ? Math.max(...allTemps) : 100;

  // Get predicted range from first anomaly marker (if any)
  const predictedMinF = anomalyMarkers[0]?.predicted_min_f || 0;
  const predictedMaxF = anomalyMarkers[0]?.predicted_max_f || 0;

  const series: any[] = [];

  // Compressor outlet line (from InfluxDB)
  if (showOutlet && outletChartData.length > 0) {
    series.push({
      name: 'Compressor Outlet',
      type: 'line',
      data: outletChartData.map(item => [item.x, item.y]),
      smooth: false,
      symbol: 'circle',
      symbolSize: 4,
      lineStyle: {
        width: 2,
        color: '#ef4444'
      },
      itemStyle: {
        color: '#ef4444'
      }
    });

  }

  // Anomaly markers (stars from Athena)
  if (anomalyMarkers.length > 0) {
    series.push({
      name: 'Anomalies',
      type: 'scatter',
      data: anomalyMarkers.map(item => [item.x, item.y]),
      symbol: 'star',
      symbolSize: 12,
      itemStyle: {
        color: '#fbbf24',
        borderColor: '#f59e0b',
        borderWidth: 2
      },
      z: 10,
      tooltip: {
        formatter: function(params: any) {
          const marker = anomalyMarkers[params.dataIndex];
          const tempF = params.data[1].toFixed(1);
          const tempC = ((params.data[1] - 32) * 5/9).toFixed(1);
          return `<b>⭐ Anomaly</b><br/>
            Time: ${new Date(params.data[0]).toLocaleString()}<br/>
            Temp: ${tempF}°F (${tempC}°C)<br/>
            Status: <span style="color: ${marker.status === 'too_high' ? '#ef4444' : '#3b82f6'}">${marker.status}</span><br/>
            Predicted Range: ${marker.predicted_min_f.toFixed(1)}°F - ${marker.predicted_max_f.toFixed(1)}°F`;
        }
      }
    });
  }

  // Compressor inlet line (from InfluxDB)
  if (showInlet && inletChartData.length > 0) {
    series.push({
      name: 'Compressor Inlet',
      type: 'line',
      data: inletChartData.map(item => [item.x, item.y]),
      smooth: false,
      symbol: 'circle',
      symbolSize: 4,
      lineStyle: {
        width: 2,
        color: '#3b82f6'
      },
      itemStyle: {
        color: '#3b82f6'
      }
    });
  }

  // Power data (from InfluxDB, on secondary Y-axis)
  if (showPower && powerChartData.length > 0) {
    series.push({
      name: 'Power',
      type: 'line',
      yAxisIndex: 1, // Use secondary Y-axis
      data: powerChartData.map(item => [item.x, item.y]),
      smooth: false,
      symbol: 'diamond',
      symbolSize: 4,
      lineStyle: {
        width: 2,
        color: '#10b981',
        type: 'dotted'
      },
      itemStyle: {
        color: '#10b981'
      }
    });
  }

  const option = {
    title: {
      text: `Raw Temperature Measurements (Past ${timeRange.replace('d', ' Day' + (timeRange === '1d' ? '' : 's'))})`,
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        let result = `<b>${new Date(params[0].data[0]).toLocaleString()}</b><br/>`;
        params.forEach((param: any) => {
          const tempF = param.data[1].toFixed(1);
          const tempC = ((param.data[1] - 32) * 5/9).toFixed(1);
          result += `${param.seriesName}: ${tempF}°F (${tempC}°C)<br/>`;
        });
        return result;
      }
    },
    legend: {
      data: ['Compressor Outlet', 'Compressor Inlet', 'Power', 'Anomalies', 'Predicted Min', 'Predicted Max'],
      top: 30
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: 80,
      containLabel: true
    },
    xAxis: {
      type: 'time',
      name: 'Time',
      nameLocation: 'middle',
      nameGap: 25
    },
    yAxis: [
      {
        type: 'value',
        name: 'Temperature (°F)',
        nameLocation: 'middle',
        nameGap: 40,
        min: minTemp - 10,
        max: maxTemp + 10
      },
      {
        type: 'value',
        name: 'Power (W)',
        nameLocation: 'middle',
        nameGap: 40,
        position: 'right'
      }
    ],
    series: [
      ...series,
      // Predicted range lines
      {
        name: 'Predicted Min',
        type: 'line',
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            position: 'end',
            formatter: `Min: ${predictedMinF.toFixed(0)}°F`
          },
          lineStyle: {
            color: '#10b981',
            type: 'dashed',
            width: 2
          },
          data: [{ yAxis: predictedMinF }]
        }
      },
      {
        name: 'Predicted Max',
        type: 'line',
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            position: 'end',
            formatter: `Max: ${predictedMaxF.toFixed(0)}°F`
          },
          lineStyle: {
            color: '#f59e0b',
            type: 'dashed',
            width: 2
          },
          data: [{ yAxis: predictedMaxF }]
        }
      }
    ],
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100
      },
      {
        type: 'slider',
        start: 0,
        end: 100,
        height: 20,
        bottom: 10
      }
    ]
  };

  return (
    <div className="space-y-4">
      {/* Toggle Controls */}
      <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg flex-wrap">
        <span className="text-sm font-medium">Show:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="checkbox" 
            checked={showOutlet}
            onChange={(e) => setShowOutlet(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm">Compressor Outlet</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="checkbox" 
            checked={showInlet}
            onChange={(e) => setShowInlet(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm">Compressor Inlet</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input 
            type="checkbox" 
            checked={showPower}
            onChange={(e) => setShowPower(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm">Power Data</span>
        </label>
        {anomalyMarkers.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-yellow-600 text-sm font-medium">
              ⭐ {anomalyMarkers.length} anomalies detected
            </span>
          </div>
        )}
      </div>

      {/* Chart */}
      <ReactECharts 
        option={option} 
        style={{ height: '400px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
};

export default RawTemperatureTimelineChart;

